import React, { createContext, useState, useContext, useEffect } from "react";
import { toast } from "sonner";
import { supabase } from "@/lib/supabase";
import { Session, User } from "@supabase/supabase-js";

// Define the structure of the users table data (from mobile app)
interface DbUserProfile {
  id: string; // This is the primary key of the users table and also user ID
  full_name?: string | null;
  email?: string | null;
  country?: string | null;
  avatar_url?: string | null;
  created_at: string;
  updated_at: string;
}

// Define the structure for the user object used in the context
interface UserProfile {
  id: string; // This should be the auth.users ID
  email: string;
  name: string;
  role: "student" | "mentor" | "admin" | "partner";
  avatarUrl?: string;
  currentSchool?: string;
  grade?: string;
  city?: string;
  age?: number;
  profileCompleted?: boolean;
}

interface AuthContextType {
  user: UserProfile | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  signup: (name: string, email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  signInWithGoogle: (options?: { intent?: "login" | "register" }) => Promise<boolean>;
  signInWithFacebook: () => Promise<boolean>;
  isAuthenticated: boolean;
  isAdmin: boolean;
  session: Session | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// For mock/fallback user data - used until Supabase auth is fully implemented
const MOCK_USERS = [
  {
    id: "user1",
    email: "student@example.com",
    name: "John Student",
    password: "password123",
    role: "student" as const,
    avatarUrl: "https://i.pravatar.cc/150?u=user1"
  },
  {
    id: "user2",
    email: "mentor@example.com",
    name: "Sarah Mentor",
    password: "password123",
    role: "mentor" as const,
    avatarUrl: "https://i.pravatar.cc/150?u=user2"
  },
  {
    id: "00000000-0000-0000-0000-000000000001", // Use a valid UUID format for mock admin
    email: "admin@example.com",
    name: "Admin User",
    password: "admin123",
    role: "admin" as const,
    avatarUrl: "https://i.pravatar.cc/150?u=admin1"
  }
];

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Check for existing session on mount and set up auth listener
  useEffect(() => {
    let isMounted = true;
    // Flag to track if we've already initialized
    let initialized = false;

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        console.log("Auth state changed:", event, newSession?.user?.id);

        if (!isMounted) return;

        if (event === 'INITIAL_SESSION') {
          // Only process INITIAL_SESSION if we haven't already initialized
          if (initialized) {
            console.log("Already initialized, skipping INITIAL_SESSION");
            return;
          }
          initialized = true;
        }

        setSession(newSession);

        if (newSession?.user) {
          // Fetch user profile data including role
          await fetchUserProfile(newSession.user);
        } else {
          setUser(null);
        }

        // Set loading to false after first event
        if (event === 'INITIAL_SESSION') {
          setLoading(false);
        }
      }
    );

    // Then check if we need to get initial session (fallback)
    const initializeAuth = async () => {
      try {
        const { data: { session: initialSession } } = await supabase.auth.getSession();

        if (!isMounted || initialized) return;

        // If we got a session but haven't initialized yet, process it
        if (initialSession) {
          initialized = true;
          setSession(initialSession);
          await fetchUserProfile(initialSession.user);
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const fetchUserProfile = async (authUser: User) => {
    try {
      console.log("Fetching user profile for:", authUser.id);
      
      // First check if user is in partners table
      const { data: partnerData } = await supabase
        .from('partners')
        .select('*')
        .eq('id', authUser.id)
        .maybeSingle();

      if (partnerData) {
        // User is a partner!
        setUser({
          id: authUser.id,
          email: authUser.email || '',
          name: partnerData?.institution_name || authUser.user_metadata?.name || 'Partner',
          role: 'partner',
          avatarUrl: partnerData?.logo_url || authUser.user_metadata?.avatar_url,
          profileCompleted: true,
        });
        console.log("Partner profile state updated from DB:", partnerData);
        return;
      }

      // If not partner, check users table (students from mobile app)
      const { data, error } = await supabase
        .from('users' as any)
        .select('*')
        .eq('id', authUser.id)
        .single<DbUserProfile>();

      if (error) {
        // Handle any error gracefully (table not found, RLS, etc.)
        console.log("No user record found or error fetching from users table:", error);
        // Set user state based only on auth data
        setUser({
          id: authUser.id,
          email: authUser.email || '',
          name: authUser.user_metadata?.name || 'User',
          role: (authUser.app_metadata?.role as UserProfile["role"]) || 'student',
          avatarUrl: authUser.user_metadata?.avatar_url,
          profileCompleted: false,
        });
        return;
      }

      // If data is successfully fetched (user exists in users table)
      const profileData = data;
      setUser({
        id: authUser.id,
        email: authUser.email || '',
        name: profileData?.full_name || authUser.user_metadata?.name || 'User',
        role: (authUser.app_metadata?.role as UserProfile["role"]) || 'student',
        avatarUrl: profileData?.avatar_url || authUser.user_metadata?.avatar_url,
        city: profileData?.country ?? undefined,
        profileCompleted: !!profileData?.full_name && !!profileData?.email,
      });
      console.log("User profile state updated from DB:", { completed: !!profileData?.full_name });

    } catch (err) {
      // Catch any unexpected errors during the try block
      console.error("Unhandled error in fetchUserProfile:", err);
      // Final fallback - use auth data only
      setUser({
        id: authUser.id,
        email: authUser.email || '',
        name: authUser.user_metadata?.name || 'User',
        role: (authUser.app_metadata?.role as UserProfile["role"]) || 'student',
        avatarUrl: authUser.user_metadata?.avatar_url,
        profileCompleted: false,
      });
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    setLoading(true);
    try {
      // Try to sign in with Supabase
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) {
        console.error("=== FULL LOGIN ERROR ===");
        console.error("Error object:", error);
        console.error("Error message:", error.message);
        console.error("Error code:", error.code);
        console.error("Error status:", error.status);
        console.error("========================");
        
        setLoading(false);
        
        // Handle specific login error codes
        let errorMessage = "An error occurred during login.";
        
        if (error.code === 'invalid_credentials') {
          errorMessage = "Invalid email or password! Please check your credentials and try again.";
        } else if (error.code === 'email_not_confirmed') {
          errorMessage = "Please verify your email address before logging in!";
        } else if (error.code === 'user_not_found') {
          errorMessage = "No account found with this email! Please register first.";
        } else {
          errorMessage = error.message || errorMessage;
        }
        
        toast.error(errorMessage);
        return false;
      }

      // Login successful with Supabase
      console.log("Supabase login successful:", data.user?.email);
      // User profile will be fetched by onAuthStateChange listener
      setLoading(false);
      return true;

    } catch (e: any) {
      console.error("Login function error:", e);
      setLoading(false);
      toast.error("An unexpected error occurred");
      return false;
    }
  };

  const signup = async (name: string, email: string, password: string): Promise<boolean> => {
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name,
            avatar_url: `https://i.pravatar.cc/150?u=${Date.now()}`
          },
        }
      });

      if (error) {
        toast.error(error.message || "An error occurred during signup");
        console.error("Signup error:", error);
        setLoading(false);
        return false;
      }

      toast.success("Account created successfully! Please check your email to verify.");
      return true;
    } catch (error: any) {
      console.error("Signup error:", error);
      toast.error("An error occurred during signup");
      setLoading(false);
      return false;
    }
  };

  const logout = async () => {
    try {
      await supabase.auth.signOut();
      localStorage.removeItem("skutopia_user");
      setUser(null);
      toast.info("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Error logging out");
    }
  };

  // Function to manually refresh the user profile data
  const refreshUserProfile = async () => {
    const { data: { user: authUser } } = await supabase.auth.getUser();
    if (authUser) {
      console.log("Refreshing user profile...");
      await fetchUserProfile(authUser);
    } else {
      console.log("No authenticated user found during refresh.");
      setUser(null);
    }
  };

  const signInWithGoogle = async (
    options?: { intent?: "login" | "register" },
  ): Promise<boolean> => {
    setLoading(true);
    try {
      sessionStorage.setItem(
        "partner_oauth_intent",
        options?.intent ?? "login",
      );
      const redirectUrl = `${window.location.origin}/auth/callback`;
      console.log('Using redirect URL:', redirectUrl);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo: redirectUrl }
      });
      if (error) throw error;
      return true;
    } catch (error: any) {
      sessionStorage.removeItem("partner_oauth_intent");
      console.error("Google Sign-In Error:", error);
      toast.error(error.message || "Failed to sign in with Google.");
      setLoading(false);
      return false;
    }
  };

  const signInWithFacebook = async (): Promise<boolean> => {
    setLoading(true);
    try {
      // Use origin to avoid hardcoding :443/:80, which can break Supabase allow-list matching.
      const redirectUrl = `${window.location.origin}/auth/callback`;
      console.log('Using redirect URL:', redirectUrl);

      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: { redirectTo: redirectUrl }
      });
      if (error) throw error;
      // Redirect happens, state managed by onAuthStateChange
      return true;
    } catch (error: any) {
      console.error("Facebook Sign-In Error:", error);
      toast.error(error.message || "Failed to sign in with Facebook.");
      setLoading(false);
      return false;
    }
  };

  const value = {
    user,
    loading,
    login,
    signup,
    logout,
    refreshUserProfile,
    signInWithGoogle,
    signInWithFacebook,
    isAuthenticated: !!session,
    isAdmin: user?.role === "admin",
    session
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
